import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Header } from '@/components'
import { Button, Card, Tabs, TabsList, TabsTrigger } from '@/components/ui'
import { AxiosError } from 'axios'
import { Flashlight, SwitchCamera } from 'lucide-react'
import { scanCheckIn, scanCheckOut } from '../../../core/services/scan-tickets'
import { QrScanner, type CameraCapabilities } from './QrScanner'
import {
  ScanValidationModal,
  type TicketHolder,
  type ValidationStatus,
} from './ScanValidationModal'
import { CameraPermissionModal } from './CameraPermissionModal'
import { Icon } from '@/common/components/Icon'

type ScanTab = 'check-in' | 'check-out'

export const ScanTicketsPage: React.FC = () => {
  const navigate = useNavigate()

  const [activeTab, setActiveTab] = useState<ScanTab>('check-in')
  const [cameraFacing, setCameraFacing] = useState<'user' | 'environment'>(
    'environment',
  )
  const [paused, setPaused] = useState(false)

  const [showModal, setShowModal] = useState(false)
  const [validationStatus, setValidationStatus] =
    useState<ValidationStatus>('INVALID_TICKET')
  const [message, setMessage] = useState('')
  const [ticketHolder, setTicketHolder] = useState<TicketHolder | null>(null)

  const [showPermissionModal, setShowPermissionModal] = useState(false)
  const [scannerKey, setScannerKey] = useState(0)
  const [capabilities, setCapabilities] = useState<CameraCapabilities>({
    hasTorch: false,
    hasMultipleCameras: false,
  })

  const handleScanSuccess = useCallback(
    async (decodedText: string) => {
      if (paused) return
      setPaused(true)

      try {
        const scanFn = activeTab === 'check-in' ? scanCheckIn : scanCheckOut
        const { data } = await scanFn(decodedText)

        setValidationStatus(
          activeTab === 'check-in' ? 'CHECKED_IN' : 'CHECKED_OUT',
        )
        if (
          ['ALREADY_CHECKED_IN', 'ALREADY_CHECKED_OUT'].includes(data.status)
        ) {
          setMessage('Please Note! This QR Code were already scanned.')
        } else if (
          [
            'INVALID_TICKET',
            'INVALID_TICKET_STATUS',
            'EXPIRED_TICKET',
            'INVALID_TRANSITION_STATUS',
          ].includes(data.status)
        ) {
          setMessage('Warning, this QR Code is not valid.')
        } else {
          setMessage('')
        }
        setTicketHolder({
          name: data.name,
          status: data.status,
          email: data.email,
          phone: data.phone,
          ticket: data.ticket,
          ticketCode: data.ticketCode,
        })
        setShowModal(true)
      } catch (e) {
        const axiosError = e as AxiosError<{
          message?: string
          ticketHolder?: TicketHolder
        }>
        const status = axiosError.response?.status
        const resData = axiosError.response?.data

        if (status === 409) {
          setValidationStatus('ALREADY_CHECKED_IN')
          setMessage(
            resData?.message ||
              'Please Note! This QR Code were already scanned.',
          )
          setTicketHolder(resData?.ticketHolder ?? null)
        } else {
          setValidationStatus('INVALID_TICKET')
          setMessage(resData?.message || 'Warning, this QR Code is not valid.')
          setTicketHolder(null)
        }
        setShowModal(true)
      } finally {
        setTimeout(() => setPaused(false), 2000)
      }
    },
    [activeTab, paused],
  )

  const handleScanError = useCallback((error: string) => {
    setValidationStatus('INVALID_TICKET')
    setMessage(error)
    setTicketHolder(null)
    setShowModal(true)
  }, [])

  const handlePermissionDenied = useCallback(() => {
    setShowPermissionModal(true)
  }, [])

  const handleCameraGranted = useCallback(() => {
    setShowPermissionModal(false)
    setScannerKey((prev) => prev + 1)
  }, [])

  const handleCancel = () => {
    navigate(-1)
  }

  const toggleCamera = () => {
    setCameraFacing((prev) => (prev === 'environment' ? 'user' : 'environment'))
  }

  return (
    <>
      <Header pageTitle="Scan Tickets" />

      <Card className="p-0 !bg-monochrome-4-60 relative overflow-hidden h-[calc(100vh-120px)] flex flex-col">
        <div className="flex justify-center py-4 absolute top-0 left-0 w-full z-10">
          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as ScanTab)}
          >
            <TabsList>
              <div className="bg-monochrome-4-60 p-2.5 rounded-[20px]">
                <TabsTrigger value="check-in">Check-In</TabsTrigger>
                <TabsTrigger value="check-out">Check-Out</TabsTrigger>
              </div>
            </TabsList>
          </Tabs>
        </div>

        <div className="flex-1 relative overflow-hidden min-h-0">
          <QrScanner
            key={scannerKey}
            onScanSuccess={handleScanSuccess}
            onScanError={handleScanError}
            onPermissionDenied={handlePermissionDenied}
            onCapabilitiesChange={setCapabilities}
            cameraFacing={cameraFacing}
            paused={paused}
          />
        </div>

        <div className="flex items-center justify-center gap-4 py-6 absolute bottom-0 left-0 w-full z-10">
          {capabilities.hasTorch && (
            <Button
              variant="secondary"
              className="w-[90px] h-[44px] rounded-full p-0 bg-monochrome-120"
              onClick={() => {}}
            >
              <Icon icon="flash" width="18" height="18" viewBox="0 0 18 18" />
            </Button>
          )}
          {capabilities.hasMultipleCameras && (
            <Button
              variant="secondary"
              className="w-[90px] h-[44px] rounded-full p-0 bg-monochrome-120"
              onClick={toggleCamera}
            >
              <Icon icon="camera" width="18" height="18" viewBox="0 0 18 18" />
            </Button>
          )}
          <Button
            variant="secondary"
            className="w-[90px] h-[44px] rounded-full px-6 bg-monochrome-120"
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </div>
      </Card>

      <ScanValidationModal
        open={showModal}
        onOpenChange={setShowModal}
        status={validationStatus}
        message={message}
        ticketHolder={ticketHolder}
      />

      <CameraPermissionModal
        open={showPermissionModal}
        onOpenChange={setShowPermissionModal}
        onGranted={handleCameraGranted}
      />
    </>
  )
}
